// Brand Console -- Recommendations.
//
// AF UX audit P0 slice (docs/product/intelligence-console-ux-audit.md,
// Section 8/20) -- this page is migrated to the shared IntelligenceCard
// shell and gains real Acknowledge/Approve/Reject/Dismiss decision
// controls plus a lineage strip, reusing exactly the same
// recommendationsAPI/actionPlansAPI routes and RecommendationDecisionCard
// building blocks RecommendationBridgePanel.jsx already used (see that
// file's own comment history) -- no new backend route, no new service
// method, no duplicated decision logic. RBAC is unchanged: isAdmin/isEditor
// still come from useAuth(), the same gate RecommendationBridgePanel
// enforces, and the backend re-validates every transition independently
// regardless of what this page shows.
//
// "Related opportunity" is the recommendation's own real opportunity_id,
// resolved against the workspace's opportunities list; "Supporting
// signal(s)" are the recommendation's own real source_signal_ids,
// resolved against the workspace's signals list -- both real
// backend-stored relationships, never invented. The lineage strip omits
// either half when it does not resolve; it never renders a placeholder
// relationship.
//
// Signals/Diagnoses/Opportunities/Action Plans are NOT migrated as part
// of this slice -- only this page.

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DashboardLayout } from '@/components/DashboardLayout';
import { IntelligenceCard } from '@/components/brand/IntelligenceCard';
import { LineageStrip } from '@/components/brand/LineageStrip';
import {
  RecommendationDecisionActions,
  RecommendationActionPlanSection,
  CAN_GENERATE_PLAN_FROM,
  REC_DECISION_LABEL,
  errorDetail,
} from '@/components/brand/RecommendationDecisionCard';
import { StatusBadge, ConfidenceIndicator } from '@/components/brand/Badges';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { WorkspaceSelect } from '@/components/brand/BrandContextBar';
import { useWorkspaceSelection } from '@/hooks/useWorkspaceSelection';
import { useAuth } from '@/contexts/AuthContext';
import { recommendationsAPI, opportunitiesAPI, signalsAPI, actionPlansAPI } from '@/lib/intelligenceApi';

export default function RecommendationsPage() {
  const { clients, workspaceId, setWorkspaceId, loadingClients } = useWorkspaceSelection();
  const navigate = useNavigate();
  const { isAdmin, isEditor } = useAuth();
  const clientId = workspaceId || null;
  const noClient = !loadingClients && clients.length === 0;
  const [recommendations, setRecommendations] = useState(null); // null = loading
  const [opportunityById, setOpportunityById] = useState({});
  const [signalById, setSignalById] = useState({});
  const [error, setError] = useState(null);

  // Same decision-state shape RecommendationBridgePanel.jsx already
  // manages for the identical real routes -- kept local to this page
  // rather than shared, since the two surfaces show different
  // recommendation sets (workspace-wide here, opportunity-scoped there)
  // and have no reason to share one in-memory state.
  const [decidingId, setDecidingId] = useState(null);
  const [plansByRec, setPlansByRec] = useState({});
  const [plansLoadingFor, setPlansLoadingFor] = useState({});
  const [detectingPlanFor, setDetectingPlanFor] = useState(null);
  const [decidingPlanId, setDecidingPlanId] = useState(null);

  const loadPlansFor = useCallback((rec) => {
    if (!clientId || !CAN_GENERATE_PLAN_FROM.has(rec.status)) return;
    setPlansLoadingFor((m) => ({ ...m, [rec.id]: true }));
    actionPlansAPI.byRecommendation(clientId, rec.id)
      .then((r) => setPlansByRec((m) => ({ ...m, [rec.id]: r.data?.action_plans || [] })))
      .catch(() => setPlansByRec((m) => ({ ...m, [rec.id]: [] })))
      .finally(() => setPlansLoadingFor((m) => ({ ...m, [rec.id]: false })));
  }, [clientId]);

  const load = useCallback(() => {
    if (!clientId) return;
    setRecommendations(null);
    setError(null);
    Promise.all([
      recommendationsAPI.listForWorkspace(clientId),
      opportunitiesAPI.listForWorkspace(clientId).catch(() => ({ data: { opportunities: [] } })),
      signalsAPI.listForWorkspace(clientId).catch(() => ({ data: { signals: [] } })),
    ])
      .then(([recsRes, oppsRes, signalsRes]) => {
        const items = recsRes.data?.recommendations || [];
        setRecommendations(items);
        const oppMap = {};
        (oppsRes.data?.opportunities || []).forEach((o) => { oppMap[o.id] = o; });
        setOpportunityById(oppMap);
        const sigMap = {};
        (signalsRes.data?.signals || []).forEach((s) => { sigMap[s.id] = s; });
        setSignalById(sigMap);
        items.forEach(loadPlansFor);
      })
      .catch((e) => setError(e));
  }, [clientId, loadPlansFor]);

  useEffect(() => { load(); }, [load]);

  const upstreamMissing = clientId && !noClient && recommendations !== null && recommendations.length === 0
    && Object.keys(opportunityById).length === 0;

  // Same real recommendationsAPI[action]/actionPlansAPI[action] calls
  // RecommendationBridgePanel.jsx's own handlers make -- no new backend
  // behavior, no new route.
  const handleDecide = (recommendation, action) => {
    if (!clientId) return;
    setDecidingId(recommendation.id);
    recommendationsAPI[action](clientId, recommendation.id)
      .then((r) => {
        setRecommendations((list) => list.map((x) => (x.id === recommendation.id ? r.data : x)));
        toast.success(`Recommendation ${REC_DECISION_LABEL[action].toLowerCase()}d.`);
      })
      .catch((e) => toast.error(errorDetail(e)))
      .finally(() => setDecidingId(null));
  };

  const handleDetectPlan = (recommendation) => {
    if (!clientId) return;
    setDetectingPlanFor(recommendation.id);
    actionPlansAPI.detect(clientId, recommendation.id)
      .then((r) => {
        setPlansByRec((m) => ({ ...m, [recommendation.id]: r.data?.action_plans || [] }));
      })
      .catch((e) => toast.error(errorDetail(e)))
      .finally(() => setDetectingPlanFor(null));
  };

  const handleDecidePlan = (recommendation, plan, action) => {
    if (!clientId) return;
    setDecidingPlanId(plan.id);
    actionPlansAPI[action](clientId, plan.id)
      .then((r) => {
        setPlansByRec((m) => ({
          ...m,
          [recommendation.id]: (m[recommendation.id] || []).map((p) => (p.id === plan.id ? r.data : p)),
        }));
        toast.success(action === 'approve' ? 'Action plan approved.' : 'Action plan cancelled.');
      })
      .catch((e) => toast.error(errorDetail(e)))
      .finally(() => setDecidingPlanId(null));
  };

  return (
    <DashboardLayout>
      <div data-testid="recommendations-page" className="max-w-5xl mx-auto">
        <div className="mb-6">
          <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--accent)] mb-1">What should we do?</p>
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Recommendations</h1>
          <p className="text-[12px] text-[var(--text-secondary)] max-w-2xl">What Martian recommends, grounded in real signals and opportunities — every recommendation is a reversible experiment or investigation, never a prescribed irreversible change.</p>
        </div>

        {!noClient && clients.length > 0 && (
          <div className="mb-4">
            <WorkspaceSelect clients={clients} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} />
          </div>
        )}

        {noClient && (
          <EmptyState title="ADD A BRAND FIRST" description="Recommendations appear once Martian has a workspace and real data to reason from." />
        )}

        {!noClient && recommendations === null && !error && <LoadingState />}
        {!noClient && error && <ErrorState error={error} onRetry={load} />}

        {!noClient && !error && recommendations !== null && recommendations.length === 0 && (
          <EmptyState
            testId="recommendations-empty-state"
            title="No recommendations yet"
            description={upstreamMissing
              ? 'Recommendations are generated from opportunities, which are generated from signals — none of those exist for this workspace yet.'
              : 'Martian has open opportunities but has not generated a recommendation with enough evidence for any of them yet.'}
            whyItMatters="A recommendation only appears once real evidence clears Martian's minimum confidence threshold — never generated just to fill this page."
          />
        )}

        {!noClient && !error && recommendations !== null && recommendations.length > 0 && (
          <div className="space-y-4" data-testid="recommendations-list">
            {recommendations.map((rec) => {
              const relatedOpportunity = opportunityById[rec.opportunity_id];
              const supportingSignals = (rec.source_signal_ids || []).map((sid) => signalById[sid]).filter(Boolean);

              // Lineage strip data -- only ever the recommendation's own
              // real opportunity_id/source_signal_ids, resolved against
              // already-fetched lists. Omitted entirely when nothing
              // resolves; never a placeholder relationship.
              //
              // P1 console polish (docs/product/intelligence-console-whole-chain-audit.md,
              // P1 #2) -- both items are clickable because they are
              // gated on the SAME already-resolved real objects
              // (relatedOpportunity/supportingSignals) this page already
              // fetched to render their labels -- not on a bare id.
              // Navigates to the Opportunities/Signals page itself, the
              // same non-deep-linked pattern the pre-existing
              // Signals -> Opportunity link already uses.
              const lineageItems = [];
              if (relatedOpportunity) {
                lineageItems.push({
                  label: 'From opportunity',
                  value: relatedOpportunity.opportunity_type?.replace(/_/g, ' ') || relatedOpportunity.id,
                  testId: 'lineage-opportunity',
                  onClick: () => navigate('/growth/opportunities'),
                });
              }
              if (supportingSignals.length > 0) {
                const resolvedCount = supportingSignals.filter((s) => s.status === 'RESOLVED').length;
                lineageItems.push({
                  label: `Supported by ${supportingSignals.length} signal${supportingSignals.length === 1 ? '' : 's'}${resolvedCount > 0 ? ` (${resolvedCount} since resolved)` : ''}`,
                  value: supportingSignals.map((s) => s.title).filter(Boolean).join('; '),
                  testId: 'lineage-signals',
                  onClick: () => navigate('/growth/signals'),
                });
              }

              const plans = plansByRec[rec.id];
              const showActionPlanSection = CAN_GENERATE_PLAN_FROM.has(rec.status) && (isEditor || (plans && plans.length > 0));

              return (
                <IntelligenceCard
                  key={rec.id}
                  testId={`recommendation-card-${rec.id}`}
                  stage="RECOMMENDATION"
                  title={rec.title || rec.action}
                  badges={(
                    <>
                      {rec.priority && <StatusBadge value={rec.priority} />}
                      <StatusBadge value={rec.status} />
                    </>
                  )}
                  statementLabel="Why Martian recommends this"
                  statement={rec.rationale}
                  metrics={[
                    rec.expected_impact ? { label: 'Expected impact', value: rec.expected_impact } : null,
                    rec.effort ? { label: 'Effort', value: rec.effort } : null,
                    rec.reversibility ? { label: 'Reversibility', value: rec.reversibility.replace(/_/g, ' ') } : null,
                    rec.confidence !== undefined && rec.confidence !== null
                      ? { label: 'Confidence', value: <ConfidenceIndicator value={rec.confidence} />, testId: 'intelligence-card-confidence' }
                      : null,
                  ].filter(Boolean)}
                  lineage={<LineageStrip items={lineageItems} />}
                  decision={(
                    <>
                      <RecommendationDecisionActions
                        recommendation={rec}
                        isAdmin={isAdmin}
                        onDecide={handleDecide}
                        decidingId={decidingId}
                      />
                      {CAN_GENERATE_PLAN_FROM.has(rec.status) && !showActionPlanSection && (
                        <p className="text-[10px] text-[var(--text-tertiary)] mt-2" data-testid="action-plan-editor-role-note">An editor can turn this into an action plan.</p>
                      )}
                      {showActionPlanSection && (
                        <RecommendationActionPlanSection
                          recommendation={rec}
                          plans={plans}
                          loading={!!plansLoadingFor[rec.id]}
                          isAdmin={isAdmin}
                          onDetect={handleDetectPlan}
                          detecting={detectingPlanFor === rec.id}
                          onDecide={handleDecidePlan}
                          decidingId={decidingPlanId}
                        />
                      )}
                    </>
                  )}
                />
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
